/**
 * Safety outbox (P1-12 lane 2, kept durable for the drain: migration plan
 * §6.2 step 2(a) and step 4 "open critical events").
 *
 * Every SOS the bridge raises is written here, with its field event exactly as
 * sent (same event_id on every re-send, so the server raises it once), BEFORE
 * the first attempt. It stays `pending` until the field endpoint answers
 * applied / already_applied. The drain (offline/drain) sends this outbox FIRST
 * on every cycle, and copies every pending entry and every OPEN CRITICAL
 * event (an SOS no human has acknowledged yet) into the encrypted handoff, so
 * the MAUI app can re-raise it on its first start: the device owns an SOS
 * until a human acknowledges it (§6.2 invariant).
 *
 * Never on Redis, never sandboxed for exercises: an exercise SOS is raised and
 * labelled by the server (architecture D6), the outbox does not care.
 *
 * Status is a string (pending | delivered | rejected), never a boolean
 * is_synced: IndexedDB cannot index booleans (see db.ts, syncFlagTypes contract).
 *
 * WHOSE ENTRY IT IS: every entry carries its originator (the user and tenant
 * the event was raised under, from the event's own user_id / tenant_id stamps
 * when it has them). flush() re-sends ONLY entries of the user and tenant
 * signed in now: on a shared Tab Active, another user's undelivered SOS is
 * never sent under this user's credential; it stays in the outbox for the
 * handoff, which carries its origin to the MAUI import.
 *
 * Every write to this table bumps the drain's write counter (a Dexie hook,
 * drain-write-tracker.ts), which voids the drain receipt.
 */
import { db, type SafetyOutboxEntry, type SafetyOutboxKind } from './db';
import { originatorStamp } from './client-id';
import { invalidateReceiptNow } from './drain/drain-write-tracker';
import type { FieldEvent, FieldIngestClient, FieldEventResult } from '@/services/field-ingest.service';
import { answeredForAnotherOriginator } from '@/services/field-ingest.service';

export type { SafetyOutboxEntry, SafetyOutboxKind };

/** An SOS older than this without a human acknowledgement is no longer handed off as open. */
export const OPEN_CRITICAL_MAX_AGE_MS = 72 * 60 * 60 * 1000;
/** Delivered entries are kept this long (receipts, and the open-critical window). */
export const DELIVERED_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
/** The most events re-sent in one flush (the endpoint takes 500). */
const FLUSH_BATCH = 100;

/**
 * Kinds that stay "open" until a human acknowledges them. The bridge records
 * only SOS and check-ins in this outbox (PAR and emergency-traffic
 * acknowledgements are not recorded by the bridge, so none are claimed).
 */
const CRITICAL_KINDS: ReadonlySet<SafetyOutboxKind> = new Set(['sos']);

/** The user and tenant whose entries a flush may send (the signed-in session). */
export interface OutboxOwner {
  userId: string | null;
  tenantId: string | null;
}

/** An entry belongs to `owner` only when both its user and its tenant are known and equal. */
export function isOwnedBy(e: Pick<SafetyOutboxEntry, 'origin_user_id' | 'origin_tenant_id'>, owner: OutboxOwner | null): boolean {
  if (!owner || !owner.userId || !owner.tenantId) return false;
  const user = e.origin_user_id ?? null;
  const tenant = e.origin_tenant_id ?? null;
  return !!user && !!tenant && user.toLowerCase() === owner.userId.toLowerCase() && tenant.toLowerCase() === owner.tenantId.toLowerCase();
}

function stampOf(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** What sendSos needs from the outbox (kept narrow so the SOS path cannot depend on more). */
export interface SafetyOutboxRecorder {
  record(event: FieldEvent, kind: SafetyOutboxKind): Promise<void>;
  delivered(eventId: string, result: Pick<FieldEventResult, 'server_event_id' | 'safety_event_id'> & { via?: 'field_v1' | 'legacy' }): Promise<void>;
}

function isDelivered(status: FieldEventResult['status']): boolean {
  return status === 'applied' || status === 'already_applied';
}

export class SafetyOutboxService implements SafetyOutboxRecorder {
  constructor(private readonly now: () => number = Date.now) {}

  async record(event: FieldEvent, kind: SafetyOutboxKind): Promise<void> {
    const existing = await db.safetyOutbox.get(event.event_id);
    if (existing) return; // a re-send of the same alarm
    // Write-ahead: the drain receipt goes BEFORE the outbox changes. An SOS is
    // never held back by it: a failed removal still writes (the write counter
    // keeps a cycle in flight from writing a receipt).
    await invalidateReceiptNow().catch(() => undefined);
    // The event's own stamps (set when it was built) are its originator.
    const stamped = event as unknown as { user_id?: unknown; tenant_id?: unknown };
    const stamp = originatorStamp(stampOf(stamped.tenant_id), event.event_id);
    const userStamp = stampOf(stamped.user_id);
    await db.safetyOutbox.put({
      ...stamp,
      ...(userStamp ? { origin_user_id: userStamp } : {}),
      event_id: event.event_id,
      kind,
      event: event as unknown as Record<string, unknown>,
      status: 'pending',
      created_at: this.now(),
      attempts: 0,
    });
  }

  async delivered(
    eventId: string,
    result: Pick<FieldEventResult, 'server_event_id' | 'safety_event_id'> & { via?: 'field_v1' | 'legacy' }
  ): Promise<void> {
    await db.safetyOutbox.update(eventId, {
      status: 'delivered',
      delivered_via: result.via ?? 'field_v1',
      delivered_at: this.now(),
      server_event_id: result.server_event_id,
      safety_event_id: result.safety_event_id,
    });
  }

  /** A human acknowledged it (owned / resolved on the console). It stops being an open critical event. */
  async humanAcknowledged(eventId: string): Promise<void> {
    await db.safetyOutbox.update(eventId, { human_acknowledged_at: this.now() });
  }

  /** Entries the server has not stored yet (pending or refused for good). */
  async pending(): Promise<SafetyOutboxEntry[]> {
    const all = await db.safetyOutbox.toArray();
    return all.filter((e) => e.status !== 'delivered').sort((a, b) => a.created_at - b.created_at);
  }

  /** SOS events no human has acknowledged yet (delivered or not). */
  async openCritical(): Promise<SafetyOutboxEntry[]> {
    const cutoff = this.now() - OPEN_CRITICAL_MAX_AGE_MS;
    const all = await db.safetyOutbox.toArray();
    return all
      .filter((e) => CRITICAL_KINDS.has(e.kind) && !e.human_acknowledged_at && e.created_at >= cutoff)
      .sort((a, b) => a.created_at - b.created_at);
  }

  /**
   * Re-send every pending entry OF `owner` (the user and tenant signed in now)
   * with its original event id. Entries of anyone else, or with an incomplete
   * originator, are never sent here: they stay for the handoff. Never waits
   * for a tracker credential mint. Resolves with how many are still pending
   * (everyone's).
   */
  async flush(client: Pick<FieldIngestClient, 'postEvents'>, owner: OutboxOwner | null): Promise<number> {
    const pending = (await this.pending()).filter((e) => e.status === 'pending' && isOwnedBy(e, owner));
    for (let i = 0; i < pending.length; i += FLUSH_BATCH) {
      const chunk = pending.slice(i, i + FLUSH_BATCH);
      const outcome = await client.postEvents(
        chunk.map((e) => e.event as unknown as FieldEvent),
        { mint: false }
      );
      if (outcome.kind !== 'response' || !outcome.body) {
        for (const e of chunk) await db.safetyOutbox.update(e.event_id, { attempts: e.attempts + 1 });
        break; // unreachable / terminal: try again next cycle
      }
      for (const r of outcome.body.results) {
        const entry = chunk[r.index];
        if (!entry) continue;
        if (isDelivered(r.status) && answeredForAnotherOriginator(r)) {
          // Stored for another user or tenant than the entry's own: not delivered where it belongs.
          await db.safetyOutbox.update(entry.event_id, { attempts: entry.attempts + 1, last_error: 'ORIGINATOR_MISMATCH' });
        } else if (isDelivered(r.status)) {
          await this.delivered(entry.event_id, r);
        } else if (r.status === 'rejected' && r.retryable === false) {
          await db.safetyOutbox.update(entry.event_id, {
            status: 'rejected',
            attempts: entry.attempts + 1,
            last_error: r.code ?? 'REJECTED',
          });
        } else {
          await db.safetyOutbox.update(entry.event_id, { attempts: entry.attempts + 1, last_error: r.code ?? r.status });
        }
      }
    }
    return (await this.pending()).length;
  }

  /**
   * Drop entries the server stored long ago and that are no longer open.
   * Only DELIVERED entries are ever removed: a pending or refused one stays
   * until it is delivered or handed off by the MAUI import.
   */
  async prune(): Promise<number> {
    const cutoff = this.now() - DELIVERED_RETENTION_MS;
    const all = await db.safetyOutbox.toArray();
    const stale = all.filter(
      (e) => e.status === 'delivered' && (e.delivered_at ?? 0) < cutoff && e.created_at < this.now() - OPEN_CRITICAL_MAX_AGE_MS
    );
    if (stale.length > 0) await db.safetyOutbox.bulkDelete(stale.map((e) => e.event_id));
    return stale.length;
  }
}

export const safetyOutboxService = new SafetyOutboxService();
